import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { CreateRecordProperties, Service } from 'webapp-types';
import {
	BatchSelectObjectType,
	DateRangeSelectionId,
	DayOfTheWeek,
	DeliveryTimeDateRangeType,
	DeliveryTimeType,
	DeliveryType,
	GranulationTypeId,
	ScheduleType,
	ScorecardType,
	TimeUnit,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { createCommaDelimitedStringWithAnd, createDeliveryTypesArray } from 'webapp/utils/common';

import type { MetricSetup } from 'webapp/models-custom';
import DateRange from 'webapp/models-custom/date-range';
import { DeliveryTimeOption } from 'webapp/models-custom/delivery-time-option';
import type Scorecard from 'webapp/models-custom/scorecard';
import type ScorecardRecord from 'webapp/models/scorecard';

import ModelManagerScorecardsService from 'webapp/services/model-manager/scorecards';

interface DayOfTheWeekProperties {
	value: DayOfTheWeek;
	firstOptionLabel: string;
	firstOptionSpanLabel: string;
	secondOptionShortLabel: string;
}

export type TrackingOriginForCreateOrEdit = 'notifications' | 'metrics';

const defaultTrackingOriginForCreateOrEdit: TrackingOriginForCreateOrEdit = 'notifications';

export interface DeliveryTimeRangeOption {
	value: DeliveryTimeDateRangeType;
	label: string;
	dateRange: DateRange;
}

export default class ModelManagerMetricDigestsService extends ModelManagerScorecardsService {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	override get objectText(): string {
		return 'Metric digest';
	}

	override get objectType(): BatchSelectObjectType {
		return BatchSelectObjectType.METRIC_DIGEST;
	}

	@cached
	get savedMetricDigestRecords(): ScorecardRecord[] {
		return this.savedRecords.filter((item) => item.type === ScorecardType.METRIC_DIGEST);
	}

	get metricDigestModels(): Scorecard[] {
		return this.createModelsFromRecordsArray(this.savedMetricDigestRecords);
	}

	findMetricDigestsForMetricSetup(metricSetup: MetricSetup): Scorecard[] {
		const metricDigest = this.metricDigestModels.filter((scorecard: Scorecard) => {
			const digestDataSelect = scorecard.savedDataSelects[0];

			if (!digestDataSelect) {
				return false;
			}

			if (digestDataSelect.metricSetupKey !== metricSetup?.key) {
				return false;
			}

			// only basic filters supported in insights where this is called, so using attributeName is ok
			if (
				metricSetup.firstAttributeNameFromBasicFilters &&
				metricSetup.allowBasicDimensionValueFilters &&
				digestDataSelect.metricSetup.attributeNamesFromBasicFilters.includes(
					metricSetup.firstAttributeNameFromBasicFilters,
				)
			) {
				return false;
			}

			if (
				metricSetup.unitName &&
				metricSetup.hasUnits &&
				digestDataSelect.unitName !== metricSetup.unitName
			) {
				return false;
			}

			return true;
		});

		return metricDigest;
	}

	override createModelForMetricSetup(
		metricSetup: MetricSetup,
		origin: TrackingOriginForCreateOrEdit,
		skipTracking = false,
	) {
		const digest = this.createMetricDigest('', origin, skipTracking);
		const dataSelect = this.createDataSelectForScorecard(digest, metricSetup.metricSetupData!);
		digest.presetDataSelectsOnCreate = [dataSelect];
		return digest;
	}

	override edit(
		model: Scorecard,
		origin: TrackingOriginForCreateOrEdit,
		isFromCreateChoicePopupFlow = false,
	): void {
		this.mmNotificationsService.openEditPopup(model, origin, isFromCreateChoicePopupFlow);
	}

	override createAndEdit(origin: TrackingOriginForCreateOrEdit): void {
		const model = this.createMetricDigest('', origin);
		this.edit(model, origin);
	}

	override toggleIsActive(model: Scorecard) {
		this.analyticsService.event(TrackingEvent.METRIC_DIGEST_STATUS_CHANGE, {
			origin: 'metric_digest',
			type: model.isActive ? 'disabled' : 'enabled',
		});
		super.toggleIsActive(model);
	}

	override createAndEditForMetricSetup(
		metricSetup: MetricSetup,
		origin: TrackingOriginForCreateOrEdit,
		isFromCreateChoicePopupFlow = false,
	) {
		const model = this.createModelForMetricSetup(metricSetup, origin);
		this.edit(model, origin, isFromCreateChoicePopupFlow);
	}

	private createMetricDigest(
		title: string,
		origin: TrackingOriginForCreateOrEdit,
		skipTracking = false,
	): Scorecard {
		const activeUserId = this.currentUserService.numericId!,
			defaultProperties = {
				title,
				type: ScorecardType.METRIC_DIGEST,
				userIds: [activeUserId],
			},
			metricDigest = this.createNewModel(defaultProperties);

		if (!skipTracking) {
			this.analyticsService.event(TrackingEvent.METRIC_DIGEST_NEW, { origin });
		}

		return metricDigest;
	}

	override createDuplicatedModel(model: Scorecard) {
		const data = model.getDataForClone(false);
		const copy = this.createNewModel(data);
		copy.presetDataSelectsOnCreate = this.createDuplicatesForModelDataSelects(model);
		return copy;
	}

	deleteMetricDigest(
		metricDigest: Scorecard,
		afterDelete?: () => void,
		origin: TrackingOriginForCreateOrEdit = defaultTrackingOriginForCreateOrEdit,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const layer = this.infolayerService.loading('Deleting Metric digest...');
			metricDigest
				.delete()
				.then(() => {
					layer.ok('Metric digest deleted!');
					this.analyticsService.event(TrackingEvent.METRIC_DIGEST_DELETED, { origin });
					if (afterDelete) {
						afterDelete();
					}
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete the Metric digest "${metricDigest.title}"?`,
			text: metricDigest.deleteText,
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	trackMetricDigestCreated(
		metricDigest: Scorecard,
		origin: string,
		sourceTypeKey: string,
		metricName: string,
	): void {
		const deliveryTypes = createDeliveryTypesArray(metricDigest?.delivery),
			deliveryDay = this.constantsService.daysOfWeek.find(
				(item) => item.value === metricDigest?.scheduleDayOfWeek[0],
			)?.label,
			trackingData = {
				origin,
				delivery: createCommaDelimitedStringWithAnd(deliveryTypes, true),
				deliveryDay,
				datasource: sourceTypeKey,
				metrics: metricName,
			};
		this.analyticsService.event(TrackingEvent.METRIC_DIGEST_CREATED, trackingData);
	}

	@cached
	get defaultMetricDigestOptions(): DeliveryTimeOption[] {
		const firstDayOfTheWeek = this.accountService.firstDayOfWeek,
			firstDayOfTheWeekProperties = this.createDayOfTheWeekProperties(firstDayOfTheWeek),
			defaultMetricDigestOptions = [
				new DeliveryTimeOption(
					DeliveryTimeType.FIRST_DAY_OF_THE_WEEK_8AM,
					`<span class="time">Every ${firstDayOfTheWeekProperties.firstOptionLabel} at 8 AM</span>`,
					`Data from last week: ${firstDayOfTheWeekProperties.firstOptionSpanLabel}`,
					[firstDayOfTheWeekProperties.value],
					8,
					DeliveryTimeDateRangeType.LAST_WEEK,
				),
				new DeliveryTimeOption(
					DeliveryTimeType.FRIDAY_4PM,
					`<span class="time">Every Friday at 4 PM</span>`,
					`Data from this week: ${firstDayOfTheWeekProperties.secondOptionShortLabel} - Fri 4 PM`,
					[DayOfTheWeek.FRIDAY],
					16,
					DeliveryTimeDateRangeType.THIS_WEEK,
				),
				new DeliveryTimeOption(
					DeliveryTimeType.CUSTOM,
					'<span class="time">Custom</span>',
					undefined,
					[DayOfTheWeek.SUNDAY],
					12,
					DeliveryTimeDateRangeType.LAST_7_DAYS,
				),
			];

		return defaultMetricDigestOptions;
	}

	get defaultMetricDigestOption() {
		return this.defaultMetricDigestOptions[0];
	}

	get deliveryTimeRangeOptions(): DeliveryTimeRangeOption[] {
		const lastWeek = DateRange.createFromRangeUISelectionIdWithGranulationId(
				DateRangeSelectionId.PREVIOUS_WEEK,
				GranulationTypeId.DAILY,
			),
			weekToDate = DateRange.createFromRangeUISelectionIdWithGranulationId(
				DateRangeSelectionId.WEEK_TO_DATE,
				GranulationTypeId.DAILY,
			),
			thisWeek = DateRange.createFromRangeUISelectionIdWithGranulationId(
				DateRangeSelectionId.THIS_WEEK,
				GranulationTypeId.DAILY,
			),
			last7Days = DateRange.createLastX(7, TimeUnit.DAY, GranulationTypeId.DAILY);

		return [
			{
				value: DeliveryTimeDateRangeType.LAST_WEEK,
				label: lastWeek.rangeTitleUX,
				dateRange: lastWeek,
			},
			{
				value: DeliveryTimeDateRangeType.WEEK_TO_DATE,
				label: weekToDate.rangeTitleUX,
				dateRange: weekToDate,
			},
			{
				value: DeliveryTimeDateRangeType.THIS_WEEK,
				label: thisWeek.rangeTitleUX,
				dateRange: thisWeek,
			},
			{
				value: DeliveryTimeDateRangeType.LAST_7_DAYS,
				label: last7Days.rangeTitleUX,
				dateRange: last7Days,
			},
		];
	}

	private createDayOfTheWeekProperties(dayOfTheWeek: DayOfTheWeek): DayOfTheWeekProperties {
		let endDayValue = dayOfTheWeek - 1;
		if (endDayValue === -1) {
			endDayValue = 6;
		}

		const startDayNames = this.constantsService.getDayNames(dayOfTheWeek),
			endDayNames = this.constantsService.getDayNames(endDayValue);

		return {
			value: dayOfTheWeek,
			firstOptionLabel: startDayNames.full,
			firstOptionSpanLabel: `${startDayNames.short} - ${endDayNames.short}`,
			secondOptionShortLabel: startDayNames.short,
		};
	}

	override createNewModel(properties: CreateRecordProperties<ScorecardRecord> = {}): Scorecard {
		const defaultTitle = 'Metric digest';
		const { scheduledDays, scheduledHour } = this.defaultMetricDigestOption!;

		if (typeof properties.scheduleType === 'undefined') {
			properties.scheduleType = ScheduleType.WEEKLY;
		}
		if (typeof properties.delivery === 'undefined') {
			properties.delivery = [DeliveryType.EMAIL];
		}
		if (typeof properties.active === 'undefined') {
			properties.active = true;
		}
		if (typeof properties.scheduleDayOfWeek === 'undefined') {
			properties.scheduleDayOfWeek = scheduledDays;
		}
		if (typeof properties.scheduleHourOfDay === 'undefined') {
			properties.scheduleHourOfDay = scheduledHour;
		}

		if (!properties.title) {
			properties.title = defaultTitle;
		}

		if (typeof properties.dateRange === 'undefined') {
			const dateRange = DateRange.createFromRangeUISelectionIdWithGranulationId(
				DateRangeSelectionId.PREVIOUS_WEEK,
				GranulationTypeId.TOTAL,
			);
			properties.dateRange = dateRange.formula;
		}

		return super.createNewModel(properties);
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/metric-digests': ModelManagerMetricDigestsService;
	}
}
